<template>
    <v-container fluid class="mt-1">
        <v-row>
            <v-col cols="12">
                <v-card class="settings-card mb-5">
                    <div class="settings-card-header pa-4 d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                            <v-icon color="white" size="16" class="mr-2">mdi-file-document-multiple-outline</v-icon>
                            <span>Event Quotes</span>
                        </div>
                        <div class="d-flex align-center" style="gap:8px;">
                            <v-btn small depressed dark color="rgba(255,255,255,0.2)" @click="openSentQuotesDialog()">
                                <v-icon small left>mdi-email-search-outline</v-icon>
                                Sent Quotes ({{ sent_quotes_count }})
                            </v-btn>
                            <v-btn small depressed dark color="rgba(255,255,255,0.2)" :disabled="locked" @click="createCustomQuote()">
                                <v-icon small left>mdi-plus</v-icon>
                                Custom Quote
                            </v-btn>
                            <v-btn small depressed dark color="rgba(255,255,255,0.2)" :disabled="locked" @click="createQuickQuote()">
                                <v-icon small left>mdi-flash-outline</v-icon>
                                Quick Quote
                            </v-btn>
                        </div>
                    </div>
                    <v-data-table :headers="headers" :items="event_quotes" disable-pagination hide-default-footer
                        :sort-by.sync="sort_by" :sort-desc.sync="sort_desc" style="font-family: Manrope;">
                        <template v-slot:item="{ item }">
                            <tr>
                                <td style="text-align: center;">
                                    <v-icon v-if="hasOrganiserDepositPayment(item)" color="success"
                                        @click.stop="openPaymentDetailsDialog(item)" style="cursor: pointer;"
                                        title="View payment details">
                                        mdi-check-circle
                                    </v-icon>
                                </td>
                                <td @click="openUpdateQuoteDialog(item)" style="cursor:pointer;">
                                    {{ item.name }}
                                    <br v-if="item.location">
                                    <v-chip v-if="item.location" x-small color="#AFCFDC" class="mt-1 mb-2">
                                        {{ item.location.name }}
                                    </v-chip>
                                    <v-chip v-if="parentQuoteName(item)" x-small outlined color="#3988AA"
                                        class="mt-1 mb-2" :class="{ 'ml-1': item.location }"
                                        title="Shared services are booked once with the supplier">
                                        Variant of {{ parentQuoteName(item) }}
                                    </v-chip>
                                    <LowProfitChip :check="profit_checks[item.id]"
                                        :currency="get_selected_company.currency" />
                                </td>
                                <td @click="openUpdateQuoteDialog(item)" style="cursor:pointer;">
                                    <label v-if="item.total">{{ get_selected_company.currency }}{{
                                        item.total.toFixed(2) }}</label>
                                </td>
                                <td @click="openUpdateQuoteDialog(item)" style="cursor:pointer;">
                                    <label v-if="item.groupsize">{{ item.groupsize }}</label>
                                </td>
                                <td @click="openUpdateQuoteDialog(item)" style="cursor:pointer;">
                                    <v-chip color="success" v-if="item.date_sent">{{
                                        $date(item.date_sent).format('DD.MM.YY') }}</v-chip>
                                    <v-chip color="error" v-else>Never</v-chip>
                                </td>
                                <td @click="openUpdateQuoteDialog(item)" style="cursor:pointer;">
                                    <label>{{ $date(item.created_at).format('DD.MM.YY') }}</label>
                                </td>
                                <td style="text-align:center;">
                                    <v-tooltip bottom color="primary" v-if="item.event_id == event.id">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon size="34" v-bind="attrs" v-on="on" v-ripple class="mr-1"
                                                color="primary" :disabled="locked" @click="toggleActivateQuote(item, 0)"
                                                v-if="item.is_activated">mdi-toggle-switch</v-icon>
                                            <v-icon size="34" v-bind="attrs" v-on="on" v-ripple class="mr-1"
                                                color="primary" :disabled="locked" @click="toggleActivateQuote(item, 1)"
                                                v-else>mdi-toggle-switch-off-outline</v-icon>
                                        </template>
                                        <span v-if="item.is_activated">Deactivate</span>
                                        <span v-else>Activate</span>
                                    </v-tooltip>
                                </td>
                                <td>
                                    <div style="display:flex;align-items:center;">
                                        <v-btn small depressed dark color="#3988AA" class="mr-2 custom-buttons"
                                            :disabled="locked" @click="bookQuoteToEvent(item)" v-if="item.event_id != event.id">
                                            <v-icon small left>mdi-bookmark-outline</v-icon>Book
                                        </v-btn>
                                        <v-btn small depressed dark color="#7C7C7C" class="mr-2 custom-buttons"
                                            :disabled="locked" @click="unbookQuoteFromEvent(item)" v-else>
                                            <v-icon small left>mdi-bookmark-remove-outline</v-icon>Unbook
                                        </v-btn>
                                        <v-menu right bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn small dense icon v-bind="attrs" v-on="on">
                                                    <v-icon color="black">mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list>
                                                <v-list-item style="cursor:pointer;"
                                                    @click="openUpdateQuoteDialog(item)">
                                                    <v-list-item-title>
                                                        <v-icon>mdi-magnify</v-icon>View/Update Quote
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item style="cursor:pointer;"
                                                    @click="openPreviewQuoteDialog(item)">
                                                    <v-list-item-title>
                                                        <v-icon>mdi-navigation-variant-outline</v-icon>Send Quote
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item style="cursor:pointer;" :disabled="locked" @click="copyQuote(item)">
                                                    <v-list-item-title>
                                                        <v-icon>mdi-content-copy</v-icon>Copy Quote
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item style="cursor:pointer;" :disabled="locked"
                                                    @click="deleteQuoteDialog(item)">
                                                    <v-list-item-title>
                                                        <v-icon>mdi-delete</v-icon>Delete Quote
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-dialog v-model="update_quote_dialog" persistent scrollable max-width="1900">
                <v-card v-if="event && selected_quote" class="quote-dialog-card">
                    <div class="settings-card-header pa-5 d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                            <v-icon color="white" size="20" class="mr-2">mdi-file-document-edit-outline</v-icon>
                            <div>
                                <div class="dialog-title">View / Update Quote</div>
                                <div class="dialog-subtitle mt-1">{{ selected_quote.name }}</div>
                            </div>
                        </div>
                        <v-btn icon dark @click="closeUpdateQuoteDialog()">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-card-text class="pa-0">
                        <CustomQuote :lead_id="event.lead.id" :quote_id="selected_quote.id" />
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-row>
        <CopyQuoteDialog v-model="copy_quote_dialog" :quote_id="copy_quote_id" :lead_id="event.lead.id"
            :event_id="event.id" @copied="$emit('refresh')" />
        <ConfirmDeleteQuote :delete_quote_dialog="delete_quote_dialog" :quote_id="delete_quote_id"
            @closeDeleteQuoteDialog="closeDeleteQuoteDialog()" @deleteQuote="deleteQuote()"></ConfirmDeleteQuote>
        <ViewSentQuotes :sent_quotes_dialog="sent_quotes_dialog" :lead_id="event.lead.id"
            @closeSentQuotesDialog="closeSentQuotesDialog()">
        </ViewSentQuotes>
        <PreviewQuoteEmail :preview_dialog="preview_quote_dialog" :quote="quote_for_preview" :lead="event.lead"
            :company="get_selected_company" @closePreviewDialog="closePreviewQuoteDialog()" @sendQuote="sendQuoteEmail"
            @sendQuoteViaSms="sendQuoteViaSms" @sendQuoteViaWhatsApp="sendQuoteViaWhatsApp">
        </PreviewQuoteEmail>
        <v-dialog v-model="payment_details_dialog" max-width="600px">
            <v-card>
                <v-card-title class="text-h5 primary white--text">
                    <v-icon left color="white">mdi-cash-check</v-icon>
                    Payment Details
                </v-card-title>
                <v-card-text class="pt-4" v-if="selected_payment">
                    <v-row>
                        <v-col cols="12">
                            <v-list>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-subtitle>Quote Name</v-list-item-subtitle>
                                        <v-list-item-title class="text-h6">{{ selected_payment.quote_name
                                        }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-subtitle>Total Paid Amount</v-list-item-subtitle>
                                        <v-list-item-title class="text-h6">{{ get_selected_company.currency }}{{
                                            selected_payment.total_paid_amount.toFixed(2) }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-subtitle>Payment Date</v-list-item-subtitle>
                                        <v-list-item-title>{{ $date(selected_payment.payment_date).format('DD/MM/YYYY')
                                        }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-subtitle>Payment Gateway</v-list-item-subtitle>
                                        <v-list-item-title>{{ selected_payment.payment_gateway }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item v-if="selected_payment.reference_code">
                                    <v-list-item-content>
                                        <v-list-item-subtitle>Reference Code</v-list-item-subtitle>
                                        <v-list-item-title>{{ selected_payment.reference_code }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="payment_details_dialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <SupplierChangesPrompt :changes="supplier_changes" :event="event" @close="supplier_changes = null" />
    </v-container>
</template>

<script>
import { mapGetters } from "vuex"
import SupplierChangesPrompt from './components/SupplierChangesPrompt.vue'
import CustomQuote from '../quotes/CustomQuote.vue'
import ConfirmDeleteQuote from '../leads/components/ConfirmDeleteQuote.vue'
import CopyQuoteDialog from '../quotes/components/CopyQuoteDialog.vue'
import ViewSentQuotes from '../leads/components/ViewSentQuotes.vue'
import PreviewQuoteEmail from '../leads/components/PreviewQuoteEmail.vue'
import LowProfitChip from '../quotes/components/LowProfitChip.vue'
import { fetchProfitChecks } from '../../utils/lowProfit'
import { isBookingClosed } from '../../utils/eventStatus'

export default {
    components: {
        CustomQuote, ConfirmDeleteQuote, ViewSentQuotes, PreviewQuoteEmail, CopyQuoteDialog, LowProfitChip,
        SupplierChangesPrompt
    },
    data: () => ({
        page_name: "Quotes",
        // From the API after deleting a quote: booked suppliers to offer a cancellation email.
        supplier_changes: null,
        classes: {
        },
        headers: [
            { text: 'Paid', value: 'paid', width: '60px', sortable: false, align: 'center' },
            { text: 'Quote Name', value: 'name' },
            { text: 'Total', value: 'total' },
            { text: 'Groupsize', value: 'groupsize' },
            { text: 'Date Sent', value: 'date_sent' },
            { text: 'Created', value: 'created_at', width: '120px' },
            { text: 'Activated', value: 'is_activated', align: 'center', width: '90px' },
            { text: 'Action', value: 'action', width: '160', align: 'center' },
        ],
        sort_by: 'id',
        sort_desc: true,
        update_quote_dialog: false,
        selected_quote: null,
        delete_quote_id: null,
        delete_quote_dialog: false,
        sent_quotes_dialog: false,
        sent_quotes_count: 0,
        preview_quote_dialog: false,
        quote_for_preview: null,
        payment_details_dialog: false,
        selected_payment: null,
        copy_quote_dialog: false,
        copy_quote_id: null,
        profit_checks: {},
    }),
    created() {
    },
    async mounted() {
        this.fetchSentQuotesCount()
    },
    watch: {
        // Re-check low profit whenever the event's quotes are reloaded or change.
        profit_check_key: {
            async handler() {
                this.profit_checks = await fetchProfitChecks(this.$axios, this.event_quotes)
            },
            immediate: true,
        },
    },
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
            event: 'eventsStore/getSelectedEventGetter',
        }),
        // Booking Closed: no new, edited, booked/unbooked, (de)activated, copied or deleted quotes
        locked() {
            return isBookingClosed(this.event)
        },
        event_quotes() {
            return this.event.lead.quotes.filter((x) => x.event_id == this.event.id || !x.event_id)
        },
        profit_check_key() {
            return this.event_quotes.map((quote) => `${quote.id}:${quote.total}:${quote.updated_at}`).join(',')
        },
    },
    methods: {
        async bookQuoteToEvent(item) {
            let payload = {
                id: item.id,
                company_id: this.get_selected_company.id,
                event_id: this.event.id,
                is_booked: 1,
            }
            const result = await (await this.$axios.post(`admin/quotes/update_quote/${payload.id}`, payload)).data
            if (result.response == true) {
                this.$toast.success(result.message, { timeout: 2000, icon: true })
                this.$emit('refresh')
            } else {
                this.$toast.error(result.message)
            }
        },
        async unbookQuoteFromEvent(item) {
            let payload = {
                id: item.id,
                company_id: this.get_selected_company.id,
                event_id: null,
                is_booked: 0,
            }
            const result = await (await this.$axios.post(`admin/quotes/update_quote/${payload.id}`, payload)).data
            if (result.response == true) {
                this.$toast.success(result.message, { timeout: 2000, icon: true })
                this.$emit('refresh')
            } else {
                this.$toast.error(result.message)
            }
        },
        async toggleActivateQuote(item, status) {
            let payload = {
                id: item.id,
                company_id: this.get_selected_company.id,
                is_activated: status,
            }
            const result = await (await this.$axios.post(`admin/quotes/update_quote/${payload.id}`, payload)).data
            if (result.response == true) {
                this.$emit('refresh')
            }
        },
        openUpdateQuoteDialog(item) {
            // The quote editor has no read-only mode, so it stays shut while Booking Closed
            if (this.locked) {
                this.$toast.info('Booking Closed: change the event status to edit quotes. Use Send Quote to preview one.')
                return
            }
            this.selected_quote = item
            this.update_quote_dialog = true
        },
        closeUpdateQuoteDialog() {
            this.selected_quote = null
            this.update_quote_dialog = false
            this.$emit('refresh')
        },
        createCustomQuote() {
            this.$router.push({ name: 'CreateCustomQuote', params: { lead_id: this.event.lead.id } })
        },
        createQuickQuote() {
            this.$router.push({ name: 'CreateQuickQuote', params: { lead_id: this.event.lead.id } })
        },
        // The main package this quote is a variant of. Copies record the main package, but older
        // links can point at another variant, so follow them up (stopping at a deleted quote).
        parentQuoteName(item) {
            const quotes = this.event.lead.quotes
            let parent = null
            let parentId = item.parent_quote_id
            const seen = new Set([item.id])
            while (parentId && !seen.has(parentId)) {
                seen.add(parentId)
                const next = quotes.find(q => q.id === parentId)
                if (!next) break
                parent = next
                parentId = next.parent_quote_id
            }
            return parent ? parent.name : null
        },
        copyQuote(item) {
            this.copy_quote_id = item.id
            this.copy_quote_dialog = true
        },
        openPreviewQuoteDialog(item) {
            if (item.total == null) {
                this.$toast.error('Please edit this quote and make sure the total price is saved')
                return
            }
            this.quote_for_preview = item
            this.preview_quote_dialog = true
        },
        closePreviewQuoteDialog() {
            this.preview_quote_dialog = false
            this.quote_for_preview = null
        },
        async sendQuoteEmail(options) {
            try {
                let payload = {
                    company_id: this.get_selected_company.id,
                    lead: this.event.lead,
                    quote: this.quote_for_preview,
                    show_description: options.show_descriptions,
                    show_names: options.show_names,
                }
                const response = await this.$axios.post('admin/quotes/send_email_quote', payload)
                const result = response.data
                if (result.response == true) {
                    this.sent_quotes_count++
                    this.$toast.success(result.message, { timeout: 2000, icon: true })
                    this.$emit('refresh')
                }
                else {
                    this.$toast.error(result.message)
                }
            } catch (error) {
                this.$toast.error('Failed to send quote email')
            } finally {
                this.closePreviewQuoteDialog()
            }
        },
        async sendQuoteViaSms() {
            try {
                const payload = {
                    company_id: this.get_selected_company.id,
                    lead: this.event.lead,
                    quote: this.quote_for_preview,
                }
                const response = await this.$axios.post('admin/quotes/send_sms_quote', payload)
                const result = response.data
                if (result.response == true) {
                    this.sent_quotes_count++
                    this.$toast.success(result.message, { timeout: 2000, icon: true })
                    this.$emit('refresh')
                } else {
                    this.$toast.error(result.message)
                }
            } catch (error) {
                this.$toast.error('Failed to send quote via SMS')
            } finally {
                this.closePreviewQuoteDialog()
            }
        },
        async sendQuoteViaWhatsApp() {
            try {
                const payload = {
                    company_id: this.get_selected_company.id,
                    lead: this.event.lead,
                    quote: this.quote_for_preview,
                }
                const response = await this.$axios.post('admin/quotes/send_whatsapp_quote', payload)
                const result = response.data
                if (result.response == true) {
                    this.sent_quotes_count++
                    this.$toast.success(result.message, { timeout: 2000, icon: true })
                    this.$emit('refresh')
                } else {
                    this.$toast.error(result.message)
                }
            } catch (error) {
                this.$toast.error('Failed to send quote via WhatsApp')
            } finally {
                this.closePreviewQuoteDialog()
            }
        },
        async fetchSentQuotesCount() {
            try {
                const response = await this.$axios.get(`admin/quotes/get_quote_emails/${this.event.lead.id}`)
                if (response.data.response) {
                    this.sent_quotes_count = response.data.data.length
                }
            } catch (e) {
                // silent
            }
        },
        openSentQuotesDialog() {
            this.sent_quotes_dialog = true
        },
        closeSentQuotesDialog() {
            this.sent_quotes_dialog = false
            this.fetchSentQuotesCount()
        },
        deleteQuoteDialog(item) {
            this.delete_quote_dialog = true
            this.delete_quote_id = item.id
        },
        closeDeleteQuoteDialog() {
            this.delete_quote_dialog = false
        },
        async deleteQuote() {
            try {
                const response = await this.$axios.delete(`admin/quotes/delete_quote_with_builds/${this.delete_quote_id}/${this.get_selected_company.id}/${this.event.lead.id}`)
                const result = response.data
                if (result.response == true) {
                    this.$toast.success(result.message, { timeout: 2000, icon: true })
                    this.closeDeleteQuoteDialog()
                    this.$emit('refresh')
                    this.supplier_changes = result.supplier_changes || null
                }
                else {
                    this.$toast.error(result.message)
                }
            } catch (error) {
                // e.g. the API refuses to delete a quote that has payments against it
                this.$toast.error(error.response?.data?.message || 'Failed to delete quote')
            }
        },
        hasOrganiserDepositPayment(quote) {
            return quote.payments && quote.payments.length > 0
        },
        openPaymentDetailsDialog(quote) {
            if (quote.payments && quote.payments.length > 0) {
                const payment = quote.payments[0]
                let reference_code = ''
                try {
                    const payment_collection = JSON.parse(payment.payment_collection)
                    reference_code = payment_collection.reference_code || ''
                } catch (e) {
                    // ignore
                }
                this.selected_payment = {
                    quote_name: quote.name,
                    total_paid_amount: payment.total_paid_amount,
                    payment_date: payment.payment_date,
                    payment_gateway: payment.payment_gateway,
                    reference_code: reference_code
                }
                this.payment_details_dialog = true
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.card-format {
    border-radius: 15px !important;

    .v-data-footer {
        background-color: #E9E9E9;
        color: #7C7C7C;
        font-family: 'Work Sans';
    }

    .v-data-footer__select .v-text-field {
        background-color: white;

        .v-select__slot {
            padding-left: 10px;
        }
    }
}

.custom-small {
    color: #7C7C7C;
    font-size: 12px;
    font-weight: 600;
}

:deep(.theme--light.v-data-table tbody td) {
    height: 40px !important;
}

.label-text-color {
    color: #7C7C7C;
}

.text-proper-case {
    text-transform: capitalize;
}

.search-text-field {
    margin-top: 15px;

    .v-input__slot {
        min-height: 0 !important;
        margin-top: 8px;
    }

    .v-input__prepend-inner {
        margin-top: 5px !important;
    }
}

:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: normal;
    box-shadow: none !important;
}

:deep(.custom-buttons button) {
    padding: 8px 7px 8px 7px !important;
    border-radius: 5px;
}

.paid_row {
    background-color: #FFF1F1 !important;
}


:deep(.custom-text-field) {
    legend {
        display: none;
    }

    fieldset {
        border-color: #D3D3D3 !important;
    }

    input {
        color: #101010;
        font-family: Manrope;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }
}

::-webkit-scrollbar {
    width: 7px;
    background: white;
}

::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: #D9D9D9;
    color: #D9D9D9;
    height: 50px;
}

.label-1 {
    color: #7C7C7C;
    background-color: #F7F7F7;
    font-size: 20px;
    border: solid 1px #7C7C7C;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-2 {
    color: #EE9E25;
    background-color: #FFF3E2;
    font-size: 20px;
    border: solid 1px #EE9E25;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-3 {
    color: #669E53;
    background-color: #E6F3E2;
    font-size: 20px;
    border: solid 1px #669E53;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-4 {
    color: #AA3939;
    background-color: #FFCFCF;
    font-size: 20px;
    border: solid 1px #AA3939;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-5 {
    color: #6A4C93;
    background-color: #EFE6F8;
    font-size: 20px;
    border: solid 1px #9B7BC4;
    border-radius: 4px;
    padding: 3px 5px;
}

.custom-expansion-panels {
    max-height: 450px;
    overflow-y: scroll;
    padding-right: 9px;
    padding-bottom: 50px;
}

.custom-expansion-panel::before {
    box-shadow: none !important;
}

.custom-expansion-panel::after {
    border-top: none !important;
}

.v-expansion-panel--active,
.v-expansion-panel--active+.v-expansion-panel {
    margin-top: 0px !important;
}

.v-expansion-panel-header--active {
    border-radius: 10px 10px 0px 0px !important;
    min-height: 0px !important;
}

:deep(.custom-expansion-header) {
    border-radius: 10px;
    background: var(--Blue-Linear, #3988AA);
    color: var(--icon-color, #FFF);
    font-size: 14px !important;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;

    .v-icon {
        color: white !important;
    }
}

.custom-expansion-panel-content {
    border-radius: 0px 0px 10px 10px;
    border: 1px solid var(--Border, #E9E9E9);
    background: var(--icon-color, #FFF);
    box-shadow: 5px 5px 13px 0px rgba(240, 240, 240, 0.90), -5px -5px 10px 0px rgba(255, 255, 255, 0.90), 5px -5px 10px 0px rgba(240, 240, 240, 0.20), -5px 5px 10px 0px rgba(240, 240, 240, 0.20);
}

.settings-card {
    border-radius: 10px !important;
    overflow: hidden;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 5px 5px 13px 0px rgba(240, 240, 240, 0.90), -5px -5px 10px 0px rgba(255, 255, 255, 0.90) !important;
}

.settings-card-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
    font-family: Manrope;
    font-size: 13px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.quote-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
}

.dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
}

.dialog-subtitle {
    font-family: Manrope;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
}

.quotes-action-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background-color: #F7F9FB;
    border-bottom: 1px solid #E9E9E9;
}

.section-label {
    font-size: 11px;
    font-weight: 700;
    color: #9E9E9E;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
}
</style>